/**
 * B2 — Libro de facturas expedidas: lo que se le pasa a la gestoría. Lista TODAS las
 * emitidas (también las anuladas y las sustituidas, que son números que existen), pero los
 * totales solo suman lo que cuenta. Permite marcar qué facturas ya se pasaron.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle, CheckCheck, Download, Loader2, Undo2 } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { downloadFile } from '../../lib/download'
import { formatCents } from '../../lib/money'
import { useAuth } from '../../contexts/AuthContext'
import { Note, rateLabel, selectCls, td, tdR, th, thR } from './common'
import type { LedgerResponse } from '@toga/shared'

interface Props {
  issuerId: string
  clientId: string
  year: number
}

const PERIODS: Array<[string, string]> = [
  ['', 'Todo el año'],
  ['q1', '1.º trimestre'],
  ['q2', '2.º trimestre'],
  ['q3', '3.º trimestre'],
  ['q4', '4.º trimestre'],
  ...Array.from({ length: 12 }, (_, i): [string, string] => [
    `m${i + 1}`,
    new Date(2000, i, 1).toLocaleDateString('es-ES', { month: 'long' }),
  ]),
]

export default function LedgerTab({ issuerId, clientId, year }: Props) {
  const auth = useAuth()
  const canMark =
    auth.status === 'authenticated' && (auth.user.role === 'ADMIN' || auth.user.role === 'ABOGADO')
  const [period, setPeriod] = useState('')
  const [accounted, setAccounted] = useState<'all' | 'yes' | 'no'>('all')
  const [data, setData] = useState<LedgerResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const [tick, setTick] = useState(0)

  const qs = useMemo(() => {
    const p = new URLSearchParams({ year: String(year), accounted })
    if (issuerId) p.set('issuerId', issuerId)
    if (clientId) p.set('clientId', clientId)
    if (period.startsWith('q')) p.set('quarter', period.slice(1))
    if (period.startsWith('m')) p.set('month', period.slice(1))
    return p.toString()
  }, [year, accounted, issuerId, clientId, period])

  useEffect(() => {
    let alive = true
    setData(null)
    setError(null)
    setSelected(new Set())
    api
      .get<LedgerResponse>(`/accounting/ledger?${qs}`)
      .then((r) => alive && setData(r))
      .catch((err) => alive && setError(err instanceof Error ? err.message : 'Error al cargar'))
    return () => {
      alive = false
    }
  }, [qs, tick])

  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })

  const pendingIds = useMemo(
    () => (data?.rows ?? []).filter((r) => !r.accountedAt).map((r) => r.id),
    [data]
  )

  const mark = async (ids: string[], value: boolean) => {
    if (ids.length === 0) return
    setBusy(true)
    setError(null)
    try {
      await api.post('/accounting/mark', { ids, accounted: value })
      setTick((t) => t + 1)
    } catch (err) {
      const body =
        err instanceof ApiError ? (err.body as { error?: string } | undefined) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'No se pudo marcar'))
    } finally {
      setBusy(false)
    }
  }

  const exportCsv = async () => {
    setBusy(true)
    try {
      await downloadFile(`/accounting/ledger.csv?${qs}`, `libro-facturas-expedidas-${year}.csv`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo exportar')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <div className="px-5 py-3 border-b border-base flex flex-wrap items-center gap-2">
        <select value={period} onChange={(e) => setPeriod(e.target.value)} className={selectCls}>
          {PERIODS.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        <select
          value={accounted}
          onChange={(e) => setAccounted(e.target.value as 'all' | 'yes' | 'no')}
          className={selectCls}
        >
          <option value="all">Pasadas y pendientes</option>
          <option value="no">Pendientes de pasar a la gestoría</option>
          <option value="yes">Ya pasadas a la gestoría</option>
        </select>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canMark && (
            <>
              <button
                type="button"
                disabled={busy || selected.size === 0}
                onClick={() => void mark([...selected], true)}
                className="px-3 py-1.5 rounded-lg text-xs border border-toga-500/40 text-toga-200 hover:bg-toga-500/10 inline-flex items-center gap-1.5 disabled:opacity-40"
              >
                <CheckCheck size={13} /> Marcar {selected.size || ''} como pasadas
              </button>
              <button
                type="button"
                disabled={busy || selected.size === 0}
                onClick={() => void mark([...selected], false)}
                className="px-3 py-1.5 rounded-lg text-xs border border-base t-secondary hover:bg-hover inline-flex items-center gap-1.5 disabled:opacity-40"
              >
                <Undo2 size={13} /> Quitar marca
              </button>
            </>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => void exportCsv()}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-toga-600 to-toga-700 hover:shadow-glow-toga inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <Download size={13} /> Exportar para la gestoría (CSV)
          </button>
        </div>
      </div>

      {error && (
        <div className="m-4 text-xs rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={13} /> {error}
        </div>
      )}
      {!data && !error && (
        <div className="px-5 py-8 t-muted text-sm flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Cargando…
        </div>
      )}
      {data && data.rows.length === 0 && (
        <div className="px-5 py-10 text-center t-muted text-sm">
          No hay facturas emitidas con esos filtros.
        </div>
      )}

      {data && data.rows.length > 0 && (
        <>
          <div className="px-5 py-2 text-xs t-muted flex flex-wrap gap-x-5 gap-y-1 border-b border-base">
            <span>{data.totals.count} facturas cuentan</span>
            {data.uncountedCount > 0 && (
              <span className="text-amber-300">
                {data.uncountedCount} no suman (anuladas o sustituidas)
              </span>
            )}
            <span>
              {data.pendingAccountingCount} sin pasar a la gestoría
              {canMark && pendingIds.length > 0 && (
                <button
                  type="button"
                  className="ml-2 underline hover:text-toga-300"
                  onClick={() => setSelected(new Set(pendingIds))}
                >
                  seleccionarlas
                </button>
              )}
            </span>
            {data.truncated && (
              <span className="text-rose-300">Listado recortado: acota el periodo</span>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-base">
                  {canMark && <th className={th} />}
                  <th className={th}>Fecha</th>
                  <th className={th}>Número</th>
                  <th className={th}>Cliente</th>
                  <th className={thR}>Base</th>
                  <th className={thR}>IVA</th>
                  <th className={thR}>Suplidos</th>
                  <th className={thR}>Retención</th>
                  <th className={thR}>A cobrar</th>
                  <th className={th}>Gestoría</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr
                    key={r.id}
                    className={[
                      'border-b border-base/50 last:border-0 align-top',
                      r.counted ? '' : 'opacity-60',
                    ].join(' ')}
                  >
                    {canMark && (
                      <td className={td}>
                        <input
                          type="checkbox"
                          checked={selected.has(r.id)}
                          onChange={() => toggle(r.id)}
                          aria-label={`Seleccionar ${r.number}`}
                        />
                      </td>
                    )}
                    <td className={`${td} whitespace-nowrap t-secondary`}>
                      {new Date(r.issueDate).toLocaleDateString('es-ES')}
                    </td>
                    <td className={`${td} whitespace-nowrap`}>
                      <Link to={`/billing/${r.id}`} className="font-mono text-xs hover:underline">
                        {r.number}
                      </Link>
                      {!issuerId && <div className="t-faint text-[11px]">{r.issuerName}</div>}
                      {r.kind === 'RECTIFICATIVA' && (
                        <div className="text-[11px] text-amber-300">
                          Rectifica a {r.rectifiesNumber ?? '—'}
                        </div>
                      )}
                      {r.status === 'ANULADA' && (
                        <div className="text-[11px] text-rose-300">Anulada</div>
                      )}
                      {r.replacedByNumber && (
                        <div className="text-[11px] text-amber-300">
                          Sustituida por {r.replacedByNumber}
                        </div>
                      )}
                    </td>
                    <td className={td}>
                      <Link
                        to={`/clients/${r.clientId}?tab=facturacion`}
                        className="hover:underline"
                      >
                        {r.clientName}
                      </Link>
                      <div className="t-faint text-[11px] font-mono">
                        {r.clientNif ?? 'sin NIF'}
                      </div>
                    </td>
                    <td className={tdR}>
                      {formatCents(r.baseCents)}
                      <div className="t-faint text-[11px]">
                        {r.vat
                          .map((v) => `${rateLabel(v.rate)}: ${formatCents(v.baseCents)}`)
                          .join(' · ')}
                      </div>
                    </td>
                    <td className={tdR}>{formatCents(r.vatCents)}</td>
                    <td className={tdR}>{r.suppliedCents ? formatCents(r.suppliedCents) : '—'}</td>
                    <td className={tdR}>{r.irpfCents ? formatCents(r.irpfCents) : '—'}</td>
                    <td className={tdR}>{formatCents(r.payableCents)}</td>
                    <td className={`${td} text-xs whitespace-nowrap`}>
                      {r.accountedAt ? (
                        <span className="text-emerald-300">
                          Pasada {new Date(r.accountedAt).toLocaleDateString('es-ES')}
                        </span>
                      ) : (
                        <span className="t-faint">Pendiente</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-base font-semibold">
                  <td className={td} colSpan={canMark ? 4 : 3}>
                    Totales ({data.totals.count} facturas)
                  </td>
                  <td className={tdR}>{formatCents(data.totals.baseCents)}</td>
                  <td className={tdR}>{formatCents(data.totals.vatCents)}</td>
                  <td className={tdR}>{formatCents(data.totals.suppliedCents)}</td>
                  <td className={tdR}>{formatCents(data.totals.irpfCents)}</td>
                  <td className={tdR}>{formatCents(data.totals.payableCents)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
      <Note>
        «Base» es la base imponible sin los suplidos (tasas, procurador, notaría…), que no son
        ingreso del despacho ni base del IVA. «A cobrar» es el importe de la factura menos la
        retención de IRPF y la provisión aplicada. El CSV lleva el IVA por tipo en columnas.
      </Note>
    </div>
  )
}
